import { useCallback, useEffect, useRef, useState } from "react";
import {
  TEST_SUITES,
  TestExecution as TestExecutionType,
  TestExecutionSubjectResult,
  TestSuite,
  RolloutDocument,
} from "@finvari/deliver";
import {
  subscribeToTestExecution,
  subscribeToTestExecutionResults,
  subscribeToMostRecentTestExecution,
  subscribeToTestExecutionsForPR,
  introspectTestSuites,
} from "../firebase";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";

export function useTestSuites(rolloutId?: string) {
  const [testSuites, setTestSuites] = useState<TestSuite[]>(Object.values(TEST_SUITES));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [introspecting, setIntrospecting] = useState(false);
  const introspectAttempted = useRef(false);

  // Subscribe to rollout document to read cached test suites.
  // Auto-introspect only after the first snapshot confirms the cache is empty.
  useEffect(() => {
    if (!rolloutId) return;
    introspectAttempted.current = false;

    const db = getFirestore();
    const rolloutRef = doc(db, 'rollouts', rolloutId);

    const unsubscribe = onSnapshot(rolloutRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const rollout = snapshot.data() as RolloutDocument;

      if (rollout.testSuites && rollout.testSuites.length > 0) {
        setTestSuites(rollout.testSuites);
        setError(rollout.testSuitesError || null);
        setLoading(false);
      } else if (!introspectAttempted.current && rollout.status === 'success') {
        // Cache is empty and deploy is done — trigger introspection once
        introspectAttempted.current = true;
        setLoading(true);
        setIntrospecting(true);
        introspectTestSuites(rolloutId)
          .catch(err => {
            console.error('Failed to introspect test suites:', err);
            setError('Failed to load dynamic test suites');
          })
          .finally(() => {
            setLoading(false);
            setIntrospecting(false);
          });
      }
    });

    return () => unsubscribe();
  }, [rolloutId]);

  const refresh = useCallback(async () => {
    if (!rolloutId || introspecting) return;
    setLoading(true);
    setIntrospecting(true);
    try {
      await introspectTestSuites(rolloutId);
      setError(null);
    } catch (err) {
      console.error('Failed to refresh test suites:', err);
      setError('Failed to refresh test suites');
    } finally {
      setLoading(false);
      setIntrospecting(false);
    }
  }, [rolloutId, introspecting]);

  return { testSuites, loading, error, refresh };
}

export function useTestExecution({ testSuiteId, environmentId, executionId, rolloutId }: { testSuiteId?: string; environmentId?: string, executionId?: string | null, rolloutId?: string }) {
  const [testExecution, setTestExecution] = useState<TestExecutionType | null>(null);
  const [subjectResults, setSubjectResults] = useState<TestExecutionSubjectResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);

  // Subscribe to the execution document (either by ID or most recent)
  useEffect(() => {
    if (!executionId && !(testSuiteId && environmentId)) {
      setLoading(false);
      return () => {};
    }

    let unsubscribe: (() => void) | null = null;

    const setupSubscription = async () => {
      try {
        if (executionId) {
          // Subscribe to specific execution by ID
          unsubscribe = await subscribeToTestExecution(executionId, (data) => {
            if (data) {
              setTestExecution(data as TestExecutionType);
              setCurrentExecutionId(data.executionId);
              setError(null);
            } else {
              setError('Test execution not found');
            }
            setLoading(false);
          });
        } else if (testSuiteId && environmentId) {
          // Subscribe to most recent execution for this suite/environment
          // This will automatically update when a new execution is created
          unsubscribe = await subscribeToMostRecentTestExecution(testSuiteId, environmentId, (data) => {
            if (data) {
              setTestExecution(data as TestExecutionType);
              setCurrentExecutionId(data.executionId);
              setError(null);
            } else {
              setTestExecution(null);
              setCurrentExecutionId(null);
            }
            setLoading(false);
          }, rolloutId);
        }
      } catch (err) {
        console.error('Error setting up execution subscription:', err);
        setError('Failed to load test execution');
        setLoading(false);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [executionId, testSuiteId, environmentId, rolloutId]);

  // Subscribe to subject results when we have an execution ID
  useEffect(() => {
    if (!currentExecutionId) {
      setSubjectResults([]);
      return () => {};
    }

    let unsubscribe: (() => void) | null = null;

    const setupResultsSubscription = async () => {
      try {
        unsubscribe = await subscribeToTestExecutionResults(currentExecutionId, (results) => {
          setSubjectResults(results as TestExecutionSubjectResult[]);
        });
      } catch (err) {
        console.error('Error setting up results subscription:', err);
      }
    };

    setupResultsSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentExecutionId]);

  return { testExecution, subjectResults, loading, error };
}

export interface PRTestExecution {
  environment: string;
  testSuiteId: string;
  testSuiteName: string;
  execution: TestExecutionType;
}

// Returns the most recent test execution per testSuiteId for a given PR.
export function usePRTestExecutions(prNumber?: number) {
  const [executions, setExecutions] = useState<PRTestExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (prNumber === undefined) {
      setExecutions([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    let unsub: (() => void) | null = null;

    subscribeToTestExecutionsForPR(prNumber, (allExecutions) => {
      // Group by testSuiteId, keep only the most recent (already sorted desc by createdAt)
      const seen = new Set<string>();
      const mostRecent: PRTestExecution[] = [];
      for (const exec of allExecutions) {
        if (!seen.has(exec.testSuiteId)) {
          seen.add(exec.testSuiteId);
          mostRecent.push({
            environment: exec.environment,
            testSuiteId: exec.testSuiteId,
            testSuiteName: exec.testSuiteName || exec.testSuiteId,
            execution: exec as TestExecutionType,
          });
        }
      }
      setExecutions(mostRecent);
      setLoading(false);
    }, (err) => {
      setError(err.message || 'Failed to load test executions');
      setLoading(false);
    }).catch((err) => {
      console.error('Error setting up test execution subscription:', err);
      setError(err.message || 'Failed to load test executions');
      setLoading(false);
    }).then(u => { if (u) unsub = u; });

    return () => { if (unsub) unsub(); };
  }, [prNumber]);

  return { executions, loading, error };
}
